import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDroppable } from "@dnd-kit/core";
import { X } from "lucide-react";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { TERM_CHROME } from "@/components/storyboard/quiz/matching-definition/DraggableTerm";

const EMPTY_DROP_ZONE =
  "border-2 border-dashed border-muted-foreground/25 bg-muted/30";

function slotFrameClass(
  highlightAsDropTarget: boolean,
  isOver: boolean,
  isSlotAssigned: boolean
): string {
  // Occupied slots never show drag target chrome (even if dnd-kit reports isOver).
  if (isSlotAssigned) {
    return "border-border bg-accent/30";
  }
  if (isOver) {
    return "border-secondary bg-secondary/10 shadow-md";
  }
  if (highlightAsDropTarget) {
    return "border-secondary bg-secondary/20";
  }
  return "border-border bg-accent/30";
}

interface DroppableSentenceSlotProps {
  sentence: string;
  assignedTerm: string | null;
  onClear: () => void;
  /** Empty slots light up while a term is being dragged. */
  highlightAsDropTarget?: boolean;
}

interface SlotContentsProps {
  assignedTerm: string | null;
  onClear: () => void;
}

const SlotContents = ({ assignedTerm, onClear }: SlotContentsProps) => {
  if (!assignedTerm) {
    return (
      <span className="text-muted-foreground/60">Begriff hierher ziehen</span>
    );
  }

  return (
    <>
      <span className="min-w-0 flex-1 font-semibold whitespace-normal">
        {assignedTerm}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={onClear}
        aria-label="Zuordnung entfernen"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </>
  );
};

/**
 * Drop target for a matching definition sentence — used on the right side of matching quizzes.
 */
const DroppableSentenceSlot = ({
  sentence,
  assignedTerm,
  onClear,
  highlightAsDropTarget = false,
}: DroppableSentenceSlotProps) => {
  const slotId = (value: string) => `slot:${value}`;
  const isSlotAssigned = assignedTerm != null;
  const { setNodeRef, isOver } = useDroppable({
    id: slotId(sentence),
    data: { sentence },
    disabled: isSlotAssigned,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "space-y-3 rounded-lg border p-4 transition-all",
        slotFrameClass(highlightAsDropTarget, isOver, isSlotAssigned)
      )}
    >
      <div
        className={cn(
          "flex min-h-[44px] items-center justify-between gap-2 rounded-md px-3 py-2 transition-colors",
          isSlotAssigned
            ? cn("border-2", TERM_CHROME.selected)
            : EMPTY_DROP_ZONE
        )}
      >
        <SlotContents assignedTerm={assignedTerm} onClear={onClear} />
      </div>

      <div className="prose max-w-full prose-p:my-0 leading-relaxed font-semibold text-primary pl-3.5">
        <CourseMarkdown>{sentence}</CourseMarkdown>
      </div>
    </div>
  );
};

export default DroppableSentenceSlot;
